import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./PersonalPasswordItem.module.css";
import { useTranslation } from "react-i18next";

export function PersonalPasswordItem({
  editable,
  label,
  password,
  onSave,
  onToggleEdit,
  isEditing,
}) {
  const {t} = useTranslation();
  const [currentPassword, setCurrentPassword] = useState(""); // Keep this empty for editing
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(true); // Default to true
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(true); // Default to true

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    letter: false,
    number: false,
    symbol: false,
  });

  const validatePassword = (password) => {
    const length = password.length >= 8;
    const letter = /[a-zA-Z]/.test(password);
    const number = /[0-9]/.test(password);
    const symbol = /[!@#$%^&*:;]/.test(password);

    setPasswordRequirements({ length, letter, number, symbol });

    return length && letter && number && symbol;
  };

  useEffect(() => {
    validatePassword(currentPassword);
  }, [currentPassword]);

  const handleOnSave = () => {
    if (!currentPassword) {
      setCurrentPasswordError(t("Please enter a password"));
      return;
    } else if (!validatePassword(currentPassword)) {
      setCurrentPasswordError(t("Password does not meet the requirements"));
      return;
    } else {
      setCurrentPasswordError("");
    }

    if (!confirmPassword) {
      setConfirmPasswordError(t("Please confirm the password"));
      return;
    } else if (currentPassword !== confirmPassword) {
      setConfirmPasswordError(t("Passwords do not match"));
      return;
    } else {
      setConfirmPasswordError("");
    }

    onSave(currentPassword);
    onToggleEdit();
    setCurrentPassword("");
    setConfirmPassword("");
    setIsPasswordVisible(true); // Keep visibility true
    setIsConfirmPasswordVisible(true); // Keep visibility true
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setConfirmPassword("");
    setCurrentPasswordError("");
    setConfirmPasswordError("");
    onToggleEdit();
    setIsPasswordVisible(true); // Reset visibility to true
    setIsConfirmPasswordVisible(true); // Reset visibility to true
  };

  const allRequirementsMet =
    passwordRequirements.length &&
    passwordRequirements.letter &&
    passwordRequirements.number &&
    passwordRequirements.symbol;

  const isConfirmPasswordValid =
    confirmPassword === currentPassword && allRequirementsMet;

  return (
    <div className={styles.container}>
      {isEditing ? (
        <>
          {!allRequirementsMet && (
            <div className={styles.requirementsBox}>
              <div className={styles.requirements}>
                <ul>
                  {!passwordRequirements.length && (
                    <li className={styles.invalid}>
                      {t("Eight or more characters")}
                    </li>
                  )}
                  {!passwordRequirements.letter && (
                    <li className={styles.invalid}>{t("One letter")}</li>
                  )}
                  {!passwordRequirements.number && (
                    <li className={styles.invalid}>{t("One number")}</li>
                  )}
                  {!passwordRequirements.symbol && (
                    <li className={styles.invalid}>
                      {t("One symbol")}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          <div className={styles.inputContainer}>
            <input
              id={`${label}-password`}
              className={classNames(
                styles.input,
                currentPasswordError
                  ? styles.inputError
                  : allRequirementsMet
                  ? styles.inputValid
                  : styles.inputInvalid
              )}
              type={isPasswordVisible ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
              }}
              placeholder={t("Password")}
            />
            <div className={styles.errorContainer}>
              {currentPasswordError && (
                <span className={styles.error}>{currentPasswordError}</span>
              )}
            </div>
            <input
              id={`${label}-confirmPassword`}
              className={classNames(
                styles.input,
                confirmPasswordError
                  ? styles.inputError
                  : isConfirmPasswordValid
                  ? styles.inputValid
                  : styles.inputInvalid
              )}
              type={isConfirmPasswordVisible ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
              }}
              placeholder={t("Confirm Password")}
            />

            <div className={styles.errorContainer}>
              {confirmPasswordError && (
                <span className={styles.error}>{confirmPasswordError}</span>
              )}
            </div>
          </div>

          <div className={styles.btnContainer}>
            <button className={styles.editBtn} onClick={handleCancel}>
              {t("Cancel")}
            </button>
            <button
              className={classNames(styles.editBtn, styles.saveBtn)}
              onClick={handleOnSave}
            >
              {t("Save")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.labelContainer}>
            <div className={styles.label}>
              {label}:{" "}
              <span className={styles.info}>
                {" "}
                {"*".repeat(password.length)}{" "}
                {/* Added space before the asterisks */}
              </span>
            </div>
            <button className={styles.editBtn} onClick={onToggleEdit}>
              {t("Edit")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

PersonalPasswordItem.propTypes = {
  editable: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onToggleEdit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
};
